import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Trash2, Flag, Send, ShieldAlert, X, Ban } from "lucide-react";
import api, { fileUrl, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import ImageLightbox from "./ImageLightbox";
import { toast } from "sonner";

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}

export default function PostCard({ post, onChange, onReport }) {
  const { user } = useAuth();
  const [p, setP] = useState(post);
  const [showComments, setShowComments] = useState(false);
  const [comment, setComment] = useState("");
  const [lightbox, setLightbox] = useState(null); // index into image list
  const liked = user && p.likes?.includes(user.user_id);

  const toggleLike = async () => {
    if (!user) { toast.error("Sign in to react."); return; }
    try { const { data } = await api.post(`/posts/${p.post_id}/like`); setP(data); } catch (e) { toast.error(apiErr(e)); }
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    try { const { data } = await api.post(`/posts/${p.post_id}/comment`, { text: comment }); setP(data); setComment(""); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async () => {
    try { await api.delete(`/posts/${p.post_id}`); toast.success("Post removed"); onChange && onChange(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delComment = async (cid) => {
    try { const { data } = await api.delete(`/posts/${p.post_id}/comment/${cid}`); setP(data); toast.success("Comment removed"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const suspend = async (uid) => {
    if (!window.confirm("Put this member on a 24-hour cool-down while our team reviews? Misusing this power will remove your own account.")) return;
    try { await api.post(`/moderate/suspend/${uid}`, { hours: 24, reason: "Guardian cool-down" }); toast.success("Member suspended for 24h. Admins will review. 🛡️"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const isMod = user && (user.role === "admin" || user.is_guardian);
  const block = async (uid) => {
    if (!window.confirm("Block this person? They won't be able to message or interact with you.")) return;
    try { await api.post(`/block/${uid}`); toast.success("Blocked."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const author = p.author || {};
  return (
    <div data-testid="post-card" className="bg-card rounded-2xl border border-border p-5 shadow-sm fade-up">
      <div className="flex items-center gap-3">
        <Link to={`/u/${author.user_id}`}>
          {author.protected ? (
            <span className="w-11 h-11 rounded-full bg-gradient-to-br from-penny/60 to-copper/60 flex items-center justify-center shrink-0">
              <Heart className="w-5 h-5 text-white" fill="currentColor" />
            </span>
          ) : (
            <img src={fileUrl(author.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${author.user_id}`}
              alt="" className="w-11 h-11 rounded-full object-cover bg-secondary" />
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/u/${author.user_id}`} className="font-semibold hover:text-copper truncate block">{author.name}</Link>
          <p className="text-xs text-muted-foreground">{timeAgo(p.created_at)} ago</p>
        </div>
        <div className="flex items-center gap-1">
          {user && (user.user_id === p.user_id || isMod) && (
            <button data-testid="delete-post-btn" onClick={del} className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground hover:text-destructive">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {isMod && user.user_id !== p.user_id && (
            <button data-testid="suspend-author-btn" onClick={() => suspend(p.user_id)} title="Cool-down (24h)" className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground hover:text-copper">
              <ShieldAlert className="w-4 h-4" />
            </button>
          )}
          {user && user.user_id !== p.user_id && (
            <button data-testid="report-post-btn" onClick={() => onReport && onReport({ target_post_id: p.post_id })}
              className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground hover:text-destructive">
              <Flag className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {p.content && <p className="mt-3 text-foreground/90 whitespace-pre-wrap leading-relaxed">{p.content}</p>}
      {p.sticker && <div className="mt-2 text-5xl">{p.sticker}</div>}

      {(() => {
        const media = (p.media_items && p.media_items.length)
          ? p.media_items
          : (p.media_url ? [{ url: p.media_url, media_type: p.media_type }] : []);
        const imgs = media.filter((m) => m.media_type === "image" || m.media_type === "gif");
        const vids = media.filter((m) => m.media_type === "video");
        const imgUrls = imgs.map((m) => m.url);
        const shown = imgs.slice(0, 4);
        const extra = imgs.length - shown.length;
        return (
          <>
            {imgs.length === 1 && (
              <img data-testid="post-image-0" src={fileUrl(imgs[0].url)} alt="" onClick={() => setLightbox(0)}
                className={`mt-3 rounded-xl object-cover cursor-zoom-in ${imgs[0].media_type === "gif" ? "max-h-64 w-auto" : "w-full max-h-[28rem]"}`} />
            )}
            {imgs.length > 1 && (
              <div data-testid="post-image-grid" className={`mt-3 grid gap-1.5 ${imgs.length === 2 ? "grid-cols-2" : "grid-cols-2"}`}>
                {shown.map((m, i) => (
                  <button key={i} data-testid={`post-image-${i}`} onClick={() => setLightbox(i)}
                    className={`relative overflow-hidden rounded-xl ${imgs.length === 3 && i === 0 ? "col-span-2 aspect-[2/1]" : "aspect-square"}`}>
                    <img src={fileUrl(m.url)} alt="" className="w-full h-full object-cover hover:opacity-95 transition-opacity" />
                    {i === 3 && extra > 0 && (
                      <div className="absolute inset-0 bg-black/55 flex items-center justify-center text-white text-2xl font-bold">+{extra}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
            {vids.map((m, i) => (
              <video key={i} src={fileUrl(m.url)} controls className="mt-3 rounded-xl w-full max-h-[28rem]" />
            ))}
            {lightbox !== null && imgUrls.length > 0 && (
              <ImageLightbox images={imgUrls} startIndex={lightbox} onClose={() => setLightbox(null)} />
            )}
          </>
        );
      })()}

      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-border">
        <button data-testid="like-btn" onClick={toggleLike} className={`flex items-center gap-1.5 text-sm ${liked ? "text-copper" : "text-muted-foreground hover:text-copper"}`}>
          <Heart className={`w-5 h-5 ${liked ? "fill-copper" : ""}`} /> {p.like_count || 0}
        </button>
        <button onClick={() => setShowComments((v) => !v)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-copper">
          <MessageCircle className="w-5 h-5" /> {p.comments?.length || 0}
        </button>
      </div>

      {showComments && (
        <div className="mt-3 space-y-3">
          {p.comments?.map((c) => (
            <div key={c.comment_id} className="flex gap-2 text-sm group items-start">
              {c.protected ? (
                <span className="w-7 h-7 rounded-full bg-gradient-to-br from-penny/60 to-copper/60 flex items-center justify-center shrink-0">
                  <Heart className="w-3.5 h-3.5 text-white" fill="currentColor" />
                </span>
              ) : (
                <img src={fileUrl(c.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${c.user_id}`} alt="" className="w-7 h-7 rounded-full bg-secondary shrink-0" />
              )}
              <div className="bg-secondary rounded-2xl px-3 py-1.5 flex-1">
                <Link to={`/u/${c.user_id}`} className="font-semibold mr-2 hover:text-copper">{c.name}</Link>{c.text}
              </div>
              {user && c.user_id !== user.user_id && (
                <button data-testid={`report-comment-${c.comment_id}`} onClick={() => onReport && onReport({ target_user_id: c.user_id })} title="Report"
                  className="p-1 rounded-full text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100"><Flag className="w-3.5 h-3.5" /></button>
              )}
              {user && c.user_id !== user.user_id && (
                <button data-testid={`block-comment-${c.comment_id}`} onClick={() => block(c.user_id)} title="Block"
                  className="p-1 rounded-full text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100"><Ban className="w-3.5 h-3.5" /></button>
              )}
              {user && (isMod || c.user_id === user.user_id || p.user_id === user.user_id) && (
                <button data-testid={`delete-comment-${c.comment_id}`} onClick={() => delComment(c.comment_id)}
                  className="p-1 rounded-full text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
              )}
              {isMod && c.user_id !== user.user_id && (
                <button data-testid={`suspend-commenter-${c.comment_id}`} onClick={() => suspend(c.user_id)} title="Cool-down (24h)"
                  className="p-1 rounded-full text-muted-foreground hover:text-copper opacity-0 group-hover:opacity-100"><ShieldAlert className="w-3.5 h-3.5" /></button>
              )}
            </div>
          ))}
          {user && (
            <div className="flex gap-2">
              <Input data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder="Write a kind reply…" className="rounded-full" />
              <Button size="icon" onClick={addComment} className="bg-copper hover:bg-copper/90 rounded-full shrink-0"><Send className="w-4 h-4" /></Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
